// `<JobDetailPage>` — top-level route at `/app/jobs/:id`.
//
// Composition (UI-SPEC § 4.2.3 wireframe):
//   GlobalNav (sticky)
//   <main id="main">
//     <Link to="/jobs">Back</Link>
//     <JobDetailHeader>           ← address, status, polling indicator
//     <AgentProgressList>         ← 7 agent rows with live elapsed counter
//     <JobErrorPanel>             ← shown only when status === 'failed'
//
// Lifecycle:
//   bad-id (NaN/0/negative)        → NotFoundPanel without firing fetch
//   isLoading || !data             → glass-strong skeleton + AgentProgressListSkeleton
//   error instanceof ApiError 404  → NotFoundPanel
//   isError (other)                → "Couldn't load this job" + Retry
//   isSuccess                      → JobDetailHeader + AgentProgressList (+ JobErrorPanel if failed)
//
// Data flow:
//   useParams<{id}>() → parse to number → useJobStatus(parsedId)
//   useJobs() (cached) → reads job_name, started_at, duration_sec for the
//   header (the polling endpoint /api/jobs/:id/status doesn't carry those
//   v1.0-shape fields).
//
//   On status flip to terminal ('completed' | 'failed') we call
//   queryClient.invalidateQueries(['jobs']) once so the list page picks
//   up the freshly-completed status on next focus / navigation.
//
// Pitfall references:
//   #14 (StrictMode double-mount) — useJobStatus dedupes by ['jobStatus', id]
//   Polling stops automatically (refetchInterval returns false on terminal)

import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { GlobalNav } from '@/components/dashboard/GlobalNav';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { JobDetailHeader } from '@/components/jobs/JobDetailHeader';
import { AgentProgressList } from '@/components/jobs/AgentProgressList';
import { AgentProgressListSkeleton } from '@/components/jobs/AgentProgressListSkeleton';
import { JobErrorPanel } from '@/components/jobs/JobErrorPanel';
import { useJobStatus } from '@/hooks/useJobStatus';
import { useJobs } from '@/hooks/useJobs';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

function NotFoundPanel({ message }: { message: string }) {
  return (
    <GlassPanel
      variant="strong"
      className="p-12 text-center flex flex-col items-center gap-4"
    >
      <AlertCircle className="w-8 h-8 text-coral-500" aria-hidden="true" />
      <h1 className="text-xl font-semibold text-foam-50">Job not found</h1>
      <p className="text-base text-foam-50/70">{message}</p>
      <Link
        to="/jobs"
        className={cn(
          'bg-foam-50/15 text-foam-50 hover:bg-foam-50/20',
          'px-6 h-12 inline-flex items-center rounded-2xl font-semibold',
          'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        )}
      >
        Back to Jobs
      </Link>
    </GlassPanel>
  );
}

export function JobDetailPage() {
  const { id: rawId } = useParams<{ id: string }>();
  const parsedId = rawId ? Number.parseInt(rawId, 10) : NaN;
  // Reject anything that isn't a positive integer — and require the URL
  // segment to be purely digits (so "12abc" doesn't slip through Number.parseInt).
  const isValidId =
    rawId != null &&
    /^[0-9]+$/.test(rawId) &&
    Number.isFinite(parsedId) &&
    parsedId > 0;

  const { data, isLoading, isError, error, refetch, dataUpdatedAt } =
    useJobStatus(isValidId ? parsedId : null);

  // Read job_name + started_at + duration from the list cache (the
  // /api/jobs/:id/status payload doesn't carry these v1.0-shape fields).
  const { data: jobsList } = useJobs();
  const jobsRow = useMemo(
    () => jobsList?.find((j) => j.job_id === parsedId),
    [jobsList, parsedId],
  );

  // Document title.
  useEffect(() => {
    const previous = document.title;
    if (data?.address) {
      document.title = `${data.address} — House Hunter`;
    } else if (isValidId) {
      document.title = 'Job — House Hunter';
    } else {
      document.title = 'Job not found — House Hunter';
    }
    return () => {
      document.title = previous;
    };
  }, [data?.address, isValidId]);

  // Invalidate ['jobs'] once when status flips to terminal so the list
  // refetches lazily on next focus / navigation. Guarded by a ref so we
  // only fire the invalidation once per terminal-state observation.
  const queryClient = useQueryClient();
  const lastTerminalStatus = useRef<string | null>(null);
  useEffect(() => {
    if (!data) return;
    const isTerminal = data.status === 'completed' || data.status === 'failed';
    if (isTerminal && lastTerminalStatus.current !== data.status) {
      lastTerminalStatus.current = data.status;
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    }
  }, [data, queryClient]);

  // Bad-id branch — skip the fetch, show 404 panel.
  if (!isValidId) {
    return (
      <>
        <GlobalNav />
        <main
          id="main"
          className="min-h-screen bg-ocean-950 text-foam-50 px-4 pt-[88px] pb-6 md:px-6 md:pb-8"
        >
          <div className="max-w-[1180px] mx-auto">
            <NotFoundPanel message="This link is not a valid job id." />
          </div>
        </main>
      </>
    );
  }

  const isPolling = data?.status === 'running' || data?.status === 'pending';
  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  return (
    <>
      <GlobalNav />
      <main
        id="main"
        className="min-h-screen bg-ocean-950 text-foam-50 px-4 pt-[88px] pb-6 md:px-6 md:pb-8"
      >
        <div className="max-w-[1180px] mx-auto flex flex-col gap-6">
          <Link
            to="/jobs"
            className={cn(
              'text-sm text-foam-50/70 hover:text-foam-50',
              'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded',
              'self-start',
            )}
          >
            ← Back to Jobs
          </Link>

          {isError ? (
            error instanceof ApiError && error.status === 404 ? (
              <NotFoundPanel message="This job may have been deleted or the link is invalid." />
            ) : (
              <GlassPanel
                variant="strong"
                className="p-12 flex items-start gap-4"
                role="alert"
              >
                <AlertCircle
                  className="w-6 h-6 text-coral-500 shrink-0"
                  aria-hidden="true"
                />
                <div className="flex flex-col gap-2">
                  <h2 className="text-base font-semibold text-foam-50">
                    Couldn&apos;t load this job.
                  </h2>
                  <p className="text-sm text-foam-50/80">
                    Try reloading the page.
                  </p>
                  <button
                    type="button"
                    onClick={() => void refetch()}
                    className={cn(
                      'self-start text-sm font-semibold text-tide-400 hover:text-tide-400/80',
                      'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded',
                    )}
                  >
                    Retry
                  </button>
                </div>
              </GlassPanel>
            )
          ) : isLoading || !data ? (
            <>
              <GlassPanel
                variant="strong"
                aria-busy="true"
                className="h-32 p-6 animate-pulse"
              />
              <AgentProgressListSkeleton />
            </>
          ) : (
            <>
              <JobDetailHeader
                job={data}
                jobName={jobsRow?.job_name ?? null}
                startedAt={jobsRow?.started_at ?? null}
                durationSec={jobsRow?.duration_sec ?? null}
                isPolling={isPolling}
                lastUpdated={lastUpdated}
              />
              <AgentProgressList agentRuns={data.agents} />
              {data.status === 'failed' ? (
                <JobErrorPanel job={{ address: data.address }} />
              ) : null}
            </>
          )}
        </div>
      </main>
    </>
  );
}
